import { useState, useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X, LucideSparkles, LucideLoader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { BACKEND_URL } from "@/constants/api";
import { useAuth } from "@clerk/clerk-react";
import { buildUserPrompt } from "./buildUserPrompt";


/**
 * Modal that lets teachers review and adjust AI generation parameters before
 * submitting. Shows an editable system prompt, read-only report info, the
 * existing comment, and a free-text instructions field.
 *
 * Props:
 *   open                - controlled open state
 *   onClose             - called when the modal should close
 *   defaultSystemPrompt - initial value for the system prompt textarea (from config)
 *   studentReportInfo   - object of key/value pairs displayed read-only
 *   currentComment      - current textarea value; shown read-only as refinement context
 *   reportId            - passed to the backend
 *   onCommentGenerated  - called with the generated comment string on success
 */

const SYSTEM_PROMPT_LIMIT = 3000;
const INSTRUCTIONS_LIMIT = 1000;

// Auto-resize helper
function autoResizeTextarea(el) {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = el.scrollHeight + "px";
}

export function AICommentModal({
  open,
  onClose,
  defaultSystemPrompt,
  studentReportInfo,
  currentComment,
  reportId,
  onCommentGenerated,
}) {
  const { getToken } = useAuth();

  const [systemPrompt, setSystemPrompt] = useState(defaultSystemPrompt || "");
  const [additionalInstructions, setAdditionalInstructions] = useState("");
  const [showSystemPrompt, setShowSystemPrompt] = useState(false);

  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);

  useEffect(() => {
    if (open) {
      setSystemPrompt(defaultSystemPrompt || "");
      setAdditionalInstructions("");
      setShowSystemPrompt(false);
      setError(null);
      setErrorDetails(null);
    }
  }, [open, defaultSystemPrompt]);

  const handleGenerate = async () => {
    setError(null);
    setErrorDetails(null);

    const token = await getToken();
    if (!token) {
      setError("Sign in to use AI comment generation.");
      return;
    }

    setIsGenerating(true);

    try {
      const userPrompt = buildUserPrompt(
        currentComment,
        additionalInstructions
      );

      const res = await fetch(`${BACKEND_URL}api/report-comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reportId: reportId || "free-trial",
          systemPrompt,
          userPrompt,
          studentReportInfo: studentReportInfo || {},
        }),
      });

      if (res.status === 429) {
        const body = await res.json().catch(() => ({}));
        const retryMins = Math.ceil((body.retryAfterSeconds ?? 900) / 60);
        setError("You've sent too many requests.");
        setErrorDetails(
          `Please wait approximately ${retryMins} minute${
            retryMins !== 1 ? "s" : ""
          } before trying again.`
        );
        return;
      }

      if (res.status === 403) {
        const body = await res.json();
        setError(
          body.message ||
            "AI generation quota exceeded. Please upgrade your plan."
        );
        return;
      }

      if (!res.ok) {
        setError("Failed to generate AI comment. Please try again.");
        return;
      }

      const { comment } = await res.json();
      onCommentGenerated(comment);
    } catch {
      setError("Failed to generate AI comment. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const infoEntries = Object.entries(studentReportInfo || {}).filter(
    ([, v]) => v !== undefined && v !== null && v !== ""
  );

  return (
    <Dialog.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />

        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">

          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <Dialog.Title className="text-lg font-semibold text-slate-900">
              Generate with AI
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="rounded-sm opacity-60 hover:opacity-100">
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          <div className="space-y-6">


            {/* System Prompt (COLLAPSIBLE) */}
            <div className="space-y-1.5 border-t pt-4">
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                  System Prompt
                </label>

                <button
                  type="button"
                  onClick={() => setShowSystemPrompt((v) => !v)}
                  className="text-xs text-slate-500 hover:text-slate-700"
                >
                  {showSystemPrompt ? "Hide" : "Edit"}
                </button>
              </div>

              {!showSystemPrompt && (
                <p className="text-xs text-slate-400 italic">
                  Using default AI behavior.
                </p>
              )}

              {showSystemPrompt && (
                <>
                  <Textarea
                    value={systemPrompt}
                    onChange={(e) => {
                      const value = e.target.value.slice(
                        0,
                        SYSTEM_PROMPT_LIMIT
                      );
                      setSystemPrompt(value);
                      autoResizeTextarea(e.target);
                    }}
                    ref={(el) => el && autoResizeTextarea(el)}
                    className="resize-none text-sm font-mono overflow-hidden"
                  />

                  <p
                    className={`text-xs text-right ${
                      systemPrompt.length > SYSTEM_PROMPT_LIMIT * 0.9
                        ? "text-red-500"
                        : "text-slate-400"
                    }`}
                  >
                    {systemPrompt.length}/{SYSTEM_PROMPT_LIMIT}
                  </p>

                  <p className="text-xs text-slate-400">
                    Advanced: controls tone and style.
                  </p>
                </>
              )}
            </div>

            
            {/* Report Info */}
            {infoEntries.length > 0 && (
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block">
                  Report Info
                </label>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                    {infoEntries.map(([key, value]) => (
                      <div key={key} className="flex gap-1.5 min-w-0">
                        <dt className="text-[11px] text-slate-400 shrink-0">
                          {formatKey(key)}:
                        </dt>
                        <dd className="text-[11px] text-slate-700 font-medium truncate">
                          {String(value)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
            )}

            {/* Existing Comment */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block">
                Existing Comment
              </label>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600 min-h-[60px] whitespace-pre-wrap">
                {currentComment?.trim() ? (
                  currentComment
                ) : (
                  <span className="italic text-slate-400 text-xs">
                    No existing comment — AI will generate from scratch.
                  </span>
                )}
              </div>
            </div>



            {/* Additional Instructions (PRIMARY) */}
            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold text-slate-700 block">
                What should the AI focus on?
              </label>

              <Textarea
                value={additionalInstructions}
                onChange={(e) => {
                  const value = e.target.value.slice(0, INSTRUCTIONS_LIMIT);
                  setAdditionalInstructions(value);
                  autoResizeTextarea(e.target);
                }}
                ref={(el) => el && autoResizeTextarea(el)}
                placeholder="e.g. Focus on reading progress. Keep it to 2 sentences. Mention strengths first."
                className="resize-none text-sm overflow-hidden"
              />

              <p
                className={`text-xs text-right ${
                  additionalInstructions.length > INSTRUCTIONS_LIMIT * 0.9
                    ? "text-red-500"
                    : "text-slate-400"
                }`}
              >
                {additionalInstructions.length}/{INSTRUCTIONS_LIMIT}
              </p>
            </div>            
            

            {/* Error */}
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-3">
                <p className="text-sm font-medium text-red-700">{error}</p>
                {errorDetails && (
                  <p className="mt-1 text-xs text-red-500">{errorDetails}</p>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onClose}
                disabled={isGenerating}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleGenerate}
                disabled={isGenerating}
              >
                {isGenerating ? (
                  <LucideLoader2 size={14} className="mr-1.5 animate-spin" />
                ) : (
                  <LucideSparkles size={14} className="mr-1.5" />
                )}
                Generate
              </Button>
            </div>

          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function formatKey(key) {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}